const numberFormatter = new Intl.NumberFormat(undefined);
const compactFormatter = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

/** Stat-tile values: compact past 10k so a hero figure never wraps. */
export function formatCompact(value: number): string {
  return Math.abs(value) >= 10_000 ? compactFormatter.format(value) : numberFormatter.format(value);
}

/** Binary units, which is what a disk reports. */
export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

export function formatPercent(fraction: number, digits = 0): string {
  return `${(fraction * 100).toFixed(digits)}%`;
}

export function formatDuration(seconds: number): string {
  if (!seconds) return '—';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(rest).padStart(2, '0')}s`;
  return `${rest}s`;
}

// --- Time zones ----------------------------------------------------------------
//
// Every date the dashboard shows or asks for is read in one time zone at a time:
// the selected sub-account's own (when Settings say to follow it and exactly one
// is selected), else the agency's zone from Settings, else the browser's.

interface ZoneRules {
  /** IANA zone from Settings; null = the browser's. */
  defaultZone: string | null;
  /** Use a sub-account's own zone when it is the only one selected. */
  followSubaccount: boolean;
  /** Each sub-account's zone, as HighLevel reports it. */
  byLocation: Map<string, string>;
}

let rules: ZoneRules = { defaultZone: null, followSubaccount: true, byLocation: new Map() };
let current: string | undefined;

export function browserZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function isValidZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function setZoneRules(next: Partial<ZoneRules>) {
  rules = { ...rules, ...next };
}

/** The zone for a set of selected sub-accounts (none selected = all of them). */
export function zoneForLocations(locationIds: string[]): string {
  if (rules.followSubaccount && locationIds.length === 1) {
    const own = rules.byLocation.get(locationIds[0]!);
    if (own && isValidZone(own)) return own;
  }
  return rules.defaultZone && isValidZone(rules.defaultZone) ? rules.defaultZone : browserZone();
}

/** The zone the page on screen shows its times in; set by the app for each render. */
export function setDisplayZone(zone: string | undefined) {
  current = zone;
}

export function displayZone(): string {
  return current ?? zoneForLocations([]);
}

/** "EDT", "GMT+5:30" — the short name of a zone at a moment. */
export function zoneAbbreviation(zone = displayZone(), at = Date.now()): string {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
        .formatToParts(new Date(at))
        .find((part) => part.type === 'timeZoneName')?.value ?? zone
    );
  } catch {
    return zone;
  }
}

/** Minutes the zone's clock is ahead of UTC at a moment (EDT → -240). */
export function zoneOffsetMinutes(zone: string, at = Date.now()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(at));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return Math.round((wall - Math.floor(at / 1000) * 1000) / 60_000);
}

/** The instant a calendar day ("2026-10-21") starts in a zone, as epoch ms. */
export function startOfDayIn(isoDay: string, zone: string): number {
  const utcMidnight = Date.parse(`${isoDay}T00:00:00Z`);
  // Two passes settle days when the clocks change (the offset at midnight itself).
  let guess = utcMidnight - zoneOffsetMinutes(zone, utcMidnight) * 60_000;
  guess = utcMidnight - zoneOffsetMinutes(zone, guess) * 60_000;
  return guess;
}

/** The last millisecond of a calendar day in a zone. */
export function endOfDayIn(isoDay: string, zone: string): number {
  return startOfDayIn(addDays(isoDay, 1), zone) - 1;
}

/** A calendar day moved by whole days, without any time zone involved. */
export function addDays(isoDay: string, days: number): string {
  const [year, month, day] = isoDay.split('-').map(Number);
  return new Date(Date.UTC(year!, month! - 1, day! + days)).toISOString().slice(0, 10);
}

export function formatDateTime(epochMs: number | null): string {
  if (!epochMs) return '—';
  return new Date(epochMs).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: displayZone(),
  });
}

/** Date and time to the second, for logs. */
export function formatPreciseTime(epochMs: number): string {
  return new Date(epochMs).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: displayZone(),
  });
}

export function formatRelative(epochMs: number | null): string {
  if (!epochMs) return 'never';
  const diff = Date.now() - epochMs;
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function formatDayLabel(isoDay: string): string {
  const [year, month, day] = isoDay.split('-').map(Number);
  if (!year || !month || !day) return isoDay;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** The calendar day a moment falls on, in the display zone (or the one given). */
export function toDateInput(date: Date, zone = displayZone()): string {
  return new Date(date.getTime() + zoneOffsetMinutes(zone, date.getTime()) * 60_000).toISOString().slice(0, 10);
}

/** Today's date N days back, as a calendar day in the display zone. */
export function daysAgo(days: number): string {
  return addDays(toDateInput(new Date()), -days);
}

/** How long something has been waiting, short: "45m", "5h", "3d". */
export function formatWait(sinceMs: number | null, now = Date.now()): string {
  if (!sinceMs) return '—';
  const minutes = Math.max(0, Math.round((now - sinceMs) / 60_000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** A response time in milliseconds, short: "4m", "2.5h", "3d". */
export function formatSpan(ms: number | null): string {
  if (ms === null || ms === undefined) return '—';
  const minutes = ms / 60_000;
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}
